import { App, Button, Dropdown, Layout, Result, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isPreviewRoutePath, replacePreviewFileIdInPath } from '../../lib/previewRoutePath';
import type File from '../../interfaces/File';
import TruncatedTooltip from '../../components/Common/TruncatedTooltip';
import { Icon } from '../../components/Core/Icon';
import type { ViewerFileSecretHandle } from '../../components/Viewer/ViewerFileSecret';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { useAuth } from '../../hooks/useAuth';
import { useFilesActions } from '../../hooks/useFilesActions';
import useOpenFile from '../../hooks/useOpenFile/useOpenFile';
import useViewer from '../../hooks/useViewer';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { hasFileLimitation } from '../../utils/utils';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';

const fileService = new FileService();
// Lazy-loaded viewers/editors
const ViewerAudio = lazy(() => import('../../components/Viewer/ViewerAudio'));
const ViewerImage = lazy(() => import('../../components/Viewer/ViewerImage'));
const ViewerImageEditor = lazy(() => import('../../components/Viewer/ViewerImageEditor'));
const ViewerTextEditor = lazy(() => import('../../components/Viewer/ViewerTextEditor'));
const VideoViewer = lazy(() => import('../../components/Viewer/ViewerVideo'));
const ViewerPdf = lazy(() => import('../../components/Viewer/ViewerPdf'));
const ViewerProtectedPdf = lazy(() => import('../../components/Viewer/ViewerProtectedPdf'));
const ViewerFileSecret = lazy(() => import('../../components/Viewer/ViewerFileSecret'));
const ViewerNote = lazy(() => import('../../components/Viewer/ViewerNote'));
const ViewerEml = lazy(() => import('../../components/Viewer/ViewerEml'));
const ViewerIcs = lazy(() => import('../../components/Viewer/ViewerIcs'));
const ViewerVcf = lazy(() => import('../../components/Viewer/ViewerVcf'));
const ViewerMsg = lazy(() => import('../../components/Viewer/ViewerMsg'));
const NoteSidebar = lazy(() => import('../../components/Viewer/ViewerNote/NoteSidebar'));

export interface ViewerProps {
  /** Override route param when embedded in the bottom drawer. */
  fileId?: string | null;
  /** Fill parent (drawer body) instead of full route viewport. */
  fillParent?: boolean;
  /** Drawer embed: close sheet instead of navigating away when applicable. */
  onRequestClose?: () => void;
}

const Viewer = ({ fileId: fileIdProp, fillParent = false, onRequestClose }: ViewerProps) => {
  const {
    file,
    fileId,
    isNoteContext,
    isLoading,
    fileAccessExpiration,
    error,
    decryptedFileBlob,
    canEditFile,
    canSignFile,
    canDownloadFile,
    handleEditFile,
    handleStartSign,
    isInEditMode,
    isSignMode,
    handleExitEditMode,
    handleSaveFile,
    pdfEncryptedBytes,
    isAntiScrapingActive,
    viewerFile,
    isDirty,
    imageBlobOverride,
    pdfBlobOverride,
    pdfViewEpoch,
    pdfInitialPage,
    pdfInitialScroll,
    textViewerRef,
    imageEditorRef,
    pdfSignRef,
    handleSave,
    handleImageEditorSave,
    handleSignUnavailable,
    handleSignSaved,
    setIsDirty,
    aiFileActionsItems,
    parsedFile,
    canShareFile,
    canMoveFile,
    downloadActions,
    downloadMenuItems,
    showDownloadActions,
    handleDownloadAction,
    isOfficeFile,
    hasAcToken,
    isHtmlFile,
    isPdfFile,
    isHtmlPreviewEnabled,
    handleHtmlPreviewClick,
    refreshViewerToken,
  } = useViewer({ fileId: fileIdProp });
  const { handleShareFiles, handleMoveFiles } = useFilesActions({});
  const { copyFileLinkToClipboard } = useOpenFile();
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasPublicFileToken = searchParams.get('publicFileToken') !== null;
  // Modal embed: swap preview id in place (keeps back-stack + pushed state); full route → `/viewer/:id`.
  const handleOpenNote = useCallback(
    (id: number) => {
      if (fillParent && isPreviewRoutePath(location.pathname)) {
        navigate(replacePreviewFileIdInPath(location.pathname, id), {
          replace: true,
          state: location.state,
        });
        return;
      }
      navigate(`/viewer/${id}`);
    },
    [fillParent, location.pathname, location.state, navigate],
  );
  // App webview (`token`+`rToken` → viewerEmbed): no host chrome actions (edit/share/move/…).
  const isViewerEmbed = isViewerEmbedSession();
  const { authenticatedUser } = useAuth();
  const secretViewerRef = useRef<ViewerFileSecretHandle>(null);
  const [isMoveModalLoading, setIsMoveModalLoading] = useState(false);
  // Secrets open read-only; Edit unlocks, then Save (subheader mirrors modal footer).
  const [secretMode, setSecretMode] = useState<'view' | 'edit'>('view');
  const canEditSecret = useMemo(() => {
    if (file?.fileType !== FileTypeEnum.Secret) return false;
    if (file.isReadOnly) return false;
    if (hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileEditContent))
      return false;
    return true;
  }, [file?.fileType, file?.isReadOnly, file?.fileLimitations]);

  useEffect(() => {
    setSecretMode('view');
  }, [file?.id]);
  // Note rail collapsed state lives here (above the per-note ViewerNote) so the rail persists across switches.
  // Narrow icon rail on small screens; full NOTES list on desktop — parity with ViewerGrant.
  const [isNoteSidebarCollapsed, setIsNoteSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches,
  );
  // Sidebar rename of the open note — keep save filename in sync.
  const [noteFileNameOverride, setNoteFileNameOverride] = useState<string | null>(null);
  useEffect(() => {
    setNoteFileNameOverride(null);
  }, [file?.id]);

  /**
   * FileMoveForm disables destinations when selected file ownerId !== folder ownerId.
   * Viewer `parsedFile` uses ownerId 0 (JWT payload has no owner); load metadata before opening move.
   */
  const handleOpenMoveModal = useCallback(async () => {
    if (!parsedFile) return;
    setIsMoveModalLoading(true);
    try {
      const detail = await fileService.getFile(parsedFile.id);
      const rawOwnerId = detail.ownerId;
      const ownerId =
        typeof rawOwnerId === 'string' ? parseInt(rawOwnerId, 10) : Number(rawOwnerId);
      if (!Number.isFinite(ownerId)) {
        notification.error({
          title: t('errors.genericFetchError', { resource: t('common.file') }),
        });
        return;
      }
      const fileForMove: File = { ...parsedFile, ownerId };
      handleMoveFiles([fileForMove], undefined, undefined);
    } catch {
      notification.error({
        title: t('errors.genericFetchError', { resource: t('common.file') }),
      });
    } finally {
      setIsMoveModalLoading(false);
    }
  }, [handleMoveFiles, notification, parsedFile, t]);

  // Opportunistic pre-load of the specific viewer chunk when file type is known
  useEffect(() => {
    switch (file?.fileType) {
      case FileTypeEnum.Text:
      case FileTypeEnum.Code:
        import('../../components/Viewer/ViewerTextEditor');
        break;
      case FileTypeEnum.Image:
        import('../../components/Viewer/ViewerImage');
        import('../../components/Viewer/ViewerImageEditor');
        break;
      case FileTypeEnum.Audio:
        import('../../components/Viewer/ViewerAudio');
        break;
      case FileTypeEnum.Video:
        import('../../components/Viewer/ViewerVideo');
        break;
      case FileTypeEnum.Word:
      case FileTypeEnum.Excel:
      case FileTypeEnum.Powerpoint:
      case FileTypeEnum.Pdf:
        import('../../components/Viewer/ViewerPdf');
        break;
      case FileTypeEnum.Secret:
        import('../../components/Viewer/ViewerFileSecret');
        break;
      case FileTypeEnum.Note:
        import('../../components/Viewer/ViewerNote');
        break;
      case FileTypeEnum.Eml:
        import('../../components/Viewer/ViewerEml');
        break;
      case FileTypeEnum.Ics:
        import('../../components/Viewer/ViewerIcs');
        break;
      case FileTypeEnum.Vcf:
        import('../../components/Viewer/ViewerVcf');
        break;
      case FileTypeEnum.Msg:
        import('../../components/Viewer/ViewerMsg');
        break;
    }
  }, [file?.fileType]);

  const openFullLabel = t('common.open');
  const openFullTooltip = t('viewer.openInNewTab', { defaultValue: 'Open in a new tab' });

  const handleCopyFileLink = useCallback(() => {
    if (!parsedFile) return;
    copyFileLinkToClipboard(parsedFile);
    message.success(t('common.linkCopied'));
  }, [copyFileLinkToClipboard, message, parsedFile, t]);

  const handleOpenFull = useCallback(() => {
    if (!fileId) return;
    const url = new URL(`/viewer/${fileId}`, window.location.origin);
    if (searchParams.get('version')) url.searchParams.set('version', searchParams.get('version')!);
    const publicTok = searchParams.get('publicFileToken');
    const ac = searchParams.get('acToken');
    if (publicTok) url.searchParams.set('publicFileToken', publicTok);
    if (ac) url.searchParams.set('acToken', ac);
    if (searchParams.get('r') === '1') url.searchParams.set('r', '1');
    // Modal store may have version/tokens not reflected in route search — prefer modal snapshot.
    const modal = useAppStore.getState();
    if (fillParent && modal.viewerModalFileId === String(fileId)) {
      if (modal.viewerModalVersion) url.searchParams.set('version', modal.viewerModalVersion);
      if (modal.viewerModalPublicFileToken) {
        url.searchParams.set('publicFileToken', modal.viewerModalPublicFileToken);
      }
      if (modal.viewerModalAcToken) url.searchParams.set('acToken', modal.viewerModalAcToken);
      if (modal.viewerModalIsReadOnly) url.searchParams.set('r', '1');
    }
    onRequestClose?.();
    window.open(url.pathname + url.search, '_blank', 'noopener,noreferrer')?.focus();
  }, [fileId, fillParent, onRequestClose, searchParams]);

  return (
    <div
      className={clsx('viewer-shell', fillParent ? 'h-full min-h-0' : 'h-full')}
      data-testid={fillParent ? 'classic-viewer-fill-parent' : undefined}
    >
      {!isViewerEmbed && (
        <div data-testid="viewer-subheader" className="viewer-subheader print:hidden!">
          <div className="viewer-subheader-inner">
            <span
              className={clsx(
                'viewer-subheader-title transition-all duration-300',
                isLoading && 'blur-sm',
              )}
            >
              <TruncatedTooltip>{file?.fileName || 'File'}</TruncatedTooltip>
            </span>

            <div
              className={clsx(
                'viewer-subheader-badges divide-gray-medium divide-x',
                isLoading && 'blur-sm',
              )}
            >
              {fileAccessExpiration && (
                <span className="text-sm">
                  {t('common.expiry')}: {fileAccessExpiration}
                </span>
              )}
              {isAntiScrapingActive && (isPdfFile || isOfficeFile) && (
                <>
                  <div className="flex h-full flex-row items-center px-2">
                    <span className="hidden truncate font-medium uppercase lg:block">
                      {t('common.readOnly')}
                    </span>
                    <Tooltip title={t('common.readOnly')}>
                      <div>
                        <Icon name="EyeOffIcon" className="h-5 w-5 lg:hidden" />
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex h-full flex-row items-center px-2">
                    <span className="hidden truncate font-medium uppercase lg:block">
                      {t('common.aiScrapingProtectionOn')}
                    </span>
                    <Tooltip title={t('common.aiScrapingProtectionOn')}>
                      <div>
                        <Icon name="FileSearchIcon" className="h-5 w-5 lg:hidden" />
                      </div>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
            {/* Action buttons — scroll horizontally on narrow viewports instead of overflowing */}
            <div className="viewer-subheader-actions">
              {!hasPublicFileToken && isHtmlFile && !hasAcToken && (
                <Tooltip
                  title={
                    isHtmlPreviewEnabled
                      ? t('viewer.htmlPreview.hideButton')
                      : t('viewer.htmlPreview.button')
                  }
                >
                  <Button
                    size="small"
                    type="text"
                    disabled={isLoading}
                    aria-label={
                      isHtmlPreviewEnabled
                        ? t('viewer.htmlPreview.hideButton')
                        : t('viewer.htmlPreview.button')
                    }
                    onClick={handleHtmlPreviewClick}
                  >
                    {isHtmlPreviewEnabled ? (
                      <Icon name="EyeOffIcon" className="h-5 w-5" />
                    ) : (
                      <Icon name="EyeIcon" className="h-5 w-5" />
                    )}
                    <span className="hidden lg:block">
                      {isHtmlPreviewEnabled
                        ? t('viewer.htmlPreview.hideButton')
                        : t('viewer.htmlPreview.button')}
                    </span>
                  </Button>
                </Tooltip>
              )}
              {!hasPublicFileToken && canEditFile && !isInEditMode && !isSignMode && (
                <Tooltip title={t('common.editFile')}>
                  <Button
                    size="small"
                    type="text"
                    disabled={isLoading}
                    aria-label={t('common.editFile')}
                    onClick={handleEditFile}
                  >
                    <Icon name="FilePenLineIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('common.editFile')}</span>
                  </Button>
                </Tooltip>
              )}

              {!hasPublicFileToken && canSignFile && !isSignMode && !isInEditMode && (
                <Tooltip title={t('common.signFile')}>
                  <Button
                    size="small"
                    type="text"
                    disabled={isLoading}
                    aria-label={t('common.signFile')}
                    onClick={handleStartSign}
                  >
                    <Icon name="SignatureIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('common.signFile')}</span>
                  </Button>
                </Tooltip>
              )}

              {!hasPublicFileToken && (isInEditMode || isSignMode) && (
                <div className="border-gray-medium flex flex-row items-center gap-2 border-r pr-2">
                  <Tooltip title={t('common.save')}>
                    <Button
                      size="small"
                      type="text"
                      disabled={isLoading || !isDirty}
                      aria-label={t('a11y.saveFile')}
                      onClick={handleSave}
                    >
                      <Icon name="SaveIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{t('common.save')}</span>
                    </Button>
                  </Tooltip>
                  <Tooltip title={t('common.exitEditMode')}>
                    <Button
                      size="small"
                      type="text"
                      disabled={isLoading || isDirty}
                      aria-label={t('a11y.exitEditMode')}
                      onClick={handleExitEditMode}
                    >
                      <Icon name="XIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{t('common.exitEditMode')}</span>
                    </Button>
                  </Tooltip>
                </div>
              )}

              {!hasPublicFileToken && canEditSecret && secretMode === 'view' && !isSignMode && (
                <Tooltip title={t('secret.edit')}>
                  <Button
                    size="small"
                    type="text"
                    disabled={isLoading}
                    aria-label={t('secret.edit')}
                    onClick={() => secretViewerRef.current?.enterEdit()}
                  >
                    <Icon name="PencilIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('secret.edit')}</span>
                  </Button>
                </Tooltip>
              )}

              {!hasPublicFileToken && canEditSecret && secretMode === 'edit' && !isSignMode && (
                <>
                  <Tooltip title={t('secret.cancel')}>
                    <Button
                      size="small"
                      type="text"
                      disabled={isLoading}
                      aria-label={t('secret.cancel')}
                      onClick={() => secretViewerRef.current?.discard()}
                    >
                      <Icon name="XIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{t('secret.cancel')}</span>
                    </Button>
                  </Tooltip>
                  <Tooltip title={t('common.save')}>
                    <Button
                      size="small"
                      type="text"
                      disabled={isLoading}
                      aria-label={t('a11y.saveFile')}
                      onClick={() => secretViewerRef.current?.save()}
                    >
                      <Icon name="SaveIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{t('common.save')}</span>
                    </Button>
                  </Tooltip>
                </>
              )}

              {!hasPublicFileToken && parsedFile && canShareFile && (
                <Tooltip title={t('common.share')}>
                  <Button
                    size="small"
                    type="text"
                    aria-label={t('common.share')}
                    onClick={() => handleShareFiles([parsedFile])}
                    disabled={isLoading}
                  >
                    <Icon name="ShareIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('common.share')}</span>
                  </Button>
                </Tooltip>
              )}

              {!hasPublicFileToken && parsedFile && canMoveFile && (
                <Tooltip title={t('common.move')}>
                  <Button
                    size="small"
                    type="text"
                    disabled={isLoading || isMoveModalLoading}
                    aria-label={t('common.move')}
                    onClick={() => void handleOpenMoveModal()}
                  >
                    <Icon name="FolderInputIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('common.move')}</span>
                  </Button>
                </Tooltip>
              )}

              {hasPublicFileToken ? (
                canDownloadFile &&
                parsedFile && (
                  <Tooltip title={t('common.download')}>
                    <Button
                      size="small"
                      type="text"
                      aria-label={t('common.download')}
                      onClick={() => handleDownloadAction(FileMenuActionEnum.Download)}
                      disabled={isLoading}
                    >
                      <Icon name="FileDownIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{t('common.download')}</span>
                    </Button>
                  </Tooltip>
                )
              ) : (
                <>
                  {showDownloadActions && downloadActions.length === 1 && (
                    <Tooltip title={downloadActions[0]?.labelText || t('common.download')}>
                      <Button
                        size="small"
                        type="text"
                        aria-label={downloadActions[0]?.labelText || t('common.download')}
                        onClick={() => handleDownloadAction(downloadActions[0]!.key)}
                        disabled={isLoading}
                      >
                        {downloadActions[0]?.icon ?? (
                          <Icon name="FileDownIcon" className="h-5 w-5" />
                        )}
                        <span className="hidden lg:block">{downloadActions[0]?.label}</span>
                      </Button>
                    </Tooltip>
                  )}
                  {showDownloadActions && downloadActions.length > 1 && (
                    <Dropdown
                      menu={{
                        items: downloadMenuItems,
                        onClick: ({ key }) => handleDownloadAction(key as FileMenuActionEnum),
                      }}
                      trigger={['click']}
                      disabled={isLoading}
                      placement="bottomRight"
                    >
                      <Button
                        size="small"
                        type="text"
                        disabled={isLoading}
                        aria-label={t('common.download')}
                      >
                        <Icon name="FileDownIcon" className="h-5 w-5" />
                        <span className="hidden lg:block">{t('common.download')}</span>
                        <Icon name="ChevronDownIcon" className="h-4 w-4" />
                      </Button>
                    </Dropdown>
                  )}
                </>
              )}
              {!hasPublicFileToken && parsedFile && (
                <Tooltip title={t('common.copyLink')}>
                  <Button
                    size="small"
                    type="text"
                    aria-label={t('a11y.copyLink')}
                    onClick={handleCopyFileLink}
                    disabled={isLoading}
                    data-testid="viewer-copy-link"
                  >
                    <Icon name="CopyIcon" className="h-5 w-5" />
                    <span className="hidden lg:block">{t('common.copyLink')}</span>
                  </Button>
                </Tooltip>
              )}
              {!hasPublicFileToken && aiFileActionsItems && aiFileActionsItems.length > 0 && (
                <Dropdown menu={{ items: aiFileActionsItems }} disabled={isLoading}>
                  <div
                    //disabled={isLoading}
                    className={clsx(
                      'group relative flex! cursor-pointer items-center! gap-2! overflow-hidden! rounded-none! border-0! bg-transparent! p-0! transition-all!',
                      isLoading && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {/* TOP */}
                    <span className="bg-ai-gradient absolute top-0 left-0 h-px w-0 transition-all duration-100 group-hover:w-full" />

                    {/* RIGHT */}
                    <span className="from-ai-violet to-ai-violet absolute top-0 right-0 h-0 w-px bg-linear-to-b transition-all delay-100 duration-100 group-hover:h-full" />

                    {/* BOTTOM */}
                    <span className="bg-ai-gradient absolute right-0 bottom-0 h-px w-0 transition-all delay-200 duration-100 group-hover:w-full" />

                    {/* LEFT */}
                    <span className="from-ai-slate to-ai-slate absolute bottom-0 left-0 h-0 w-px bg-linear-to-b transition-all delay-300 duration-100 group-hover:h-full" />

                    <Icon name="BrainCircuitIcon" className="text-blue-dark ml-1 h-5 w-5" />
                    <span className="animate-gradient text-ai-gradient mr-1 hidden lg:block">
                      {t('common.AIGrant')}
                    </span>
                  </div>
                </Dropdown>
              )}

              {/* Modal/drawer embed: open-full + close live here (no separate Modal title bar). */}
              {fillParent ? (
                <>
                  <Tooltip title={openFullTooltip}>
                    <Button
                      size="small"
                      type="text"
                      aria-label={openFullTooltip}
                      onClick={handleOpenFull}
                      data-testid="viewer-embed-open-full"
                    >
                      <Icon name="ExternalLinkIcon" className="h-5 w-5" />
                      <span className="hidden lg:block">{openFullLabel}</span>
                    </Button>
                  </Tooltip>
                  {onRequestClose ? (
                    <Tooltip title={t('common.close')}>
                      <Button
                        size="small"
                        type="text"
                        aria-label={t('common.close')}
                        onClick={onRequestClose}
                        data-testid="viewer-embed-close"
                      >
                        <Icon name="XIcon" className="h-5 w-5" />
                        <span className="hidden lg:block">{t('common.close')}</span>
                      </Button>
                    </Tooltip>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div
        className={
          isNoteContext
            ? 'viewer-content viewer-content--note relative flex min-h-0 flex-1 overflow-hidden'
            : fillParent
              ? 'viewer-content'
              : 'relative flex min-h-0 flex-1 overflow-hidden'
        }
      >
        {/* Note rail: rendered here (not inside ViewerNote) so it stays mounted while switching
            notes — only the content pane to its right shows the loading spinner.
            Collapsible Sider (icon rail when collapsed); mobile expanded overlays editor. */}
        {isNoteContext && (
          <>
            {!isNoteSidebarCollapsed && (
              <button
                type="button"
                aria-label={t('common.close')}
                className="viewer-note-rail-backdrop"
                onClick={() => setIsNoteSidebarCollapsed(true)}
              />
            )}
            <Suspense
              fallback={
                <Layout.Sider
                  width={256}
                  collapsedWidth={56}
                  collapsed={isNoteSidebarCollapsed}
                  theme="light"
                  className="viewer-note-sidebar viewer-note-rail--loading"
                />
              }
            >
              <NoteSidebar
                currentFileId={Number(fileId)}
                collapsed={isNoteSidebarCollapsed}
                onCollapsedChange={setIsNoteSidebarCollapsed}
                onOpenNote={handleOpenNote}
                onNoteRenamed={(id, name) => {
                  if (id === Number(fileId)) setNoteFileNameOverride(name);
                }}
              />
            </Suspense>
          </>
        )}
        {/* fillParent / note: absolute `viewer-content-fill` like ViewerGrant — flex-1 alone
            collapses when parent `.viewer-content` is not a flex container (modal video chrome). */}
        <div
          className={clsx(
            (fillParent || isNoteContext) && 'viewer-content-fill',
            !fillParent && !isNoteContext && 'flex min-h-0 min-w-0 flex-1 flex-col',
          )}
        >
          {isLoading ? (
            fillParent ? (
              <div className="viewer-content-loading" role="status" aria-busy="true">
                <div className="flex flex-col items-center gap-2">
                  <Spin size="large" />
                  <div className="meter orange">
                    <span data-progress="0"></span>
                  </div>
                  <div id="fg-loader-text"></div>
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2">
                <Spin size="large" />
                <div className="meter orange">
                  <span data-progress="0"></span>
                </div>
                <div id="fg-loader-text"></div>
              </div>
            )
          ) : error ? (
            <div className="flex h-full w-full items-center justify-center">
              <Result
                status={error.status}
                className="flex-1"
                title={error.title}
                subTitle={error.description}
                extra={
                  <>
                    {Number(error.status) === 403 ? (
                      <div className="flex flex-wrap items-center justify-center gap-2">
                        {parsedFile && (file?.canDownload || error.canDownload) ? (
                          <Button
                            type="primary"
                            onClick={() => handleDownloadAction(FileMenuActionEnum.Download)}
                          >
                            {t('common.downloadFile')}
                          </Button>
                        ) : null}
                        <Button
                          type={
                            parsedFile && (file?.canDownload || error.canDownload)
                              ? 'default'
                              : 'primary'
                          }
                          onClick={() => {
                            switch (authenticatedUser?.role) {
                              case UserRoleEnum.Reader:
                              case UserRoleEnum.External:
                                navigate('/files/received', { replace: true });
                                break;
                              default:
                                navigate('/files/personal', { replace: true });
                                break;
                            }
                          }}
                        >
                          {t('common.goToFileManager')}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {parsedFile && (file?.canDownload || error.canDownload) ? (
                          <Button
                            onClick={() => handleDownloadAction(FileMenuActionEnum.Download)}
                            type="primary"
                          >
                            {t('common.downloadFile')}
                          </Button>
                        ) : error.canReloadPage !== false ? (
                          <Button
                            type="primary"
                            onClick={() => {
                              window.location.reload();
                            }}
                          >
                            {t('common.reload')}
                          </Button>
                        ) : null}
                      </>
                    )}
                  </>
                }
              />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex h-full w-full items-center justify-center">
                  <Spin size="large" />
                </div>
              }
            >
              <div className="flex h-full w-full overflow-hidden">
                {!file?.fileType || !viewerFile
                  ? null
                  : (() => {
                      switch (file?.fileType) {
                        case FileTypeEnum.Text:
                        case FileTypeEnum.Code:
                          return (
                            <ViewerTextEditor
                              ref={textViewerRef}
                              file={viewerFile}
                              isEditMode={isInEditMode}
                              isAntiScrapingActive={isAntiScrapingActive}
                              // Clean exit after a successful save (no unsaved-changes guard):
                              // the save handler already cleared the dirty flag, but that state update
                              // hasn't flushed yet, so a guarded exit would read a stale isDirty=true.
                              onSave={handleSaveFile}
                              onDirtyStateChange={setIsDirty}
                              onLoaded={() => {}}
                            />
                          );

                        case FileTypeEnum.Image:
                          return isInEditMode ? (
                            <ViewerImageEditor
                              ref={imageEditorRef}
                              file={viewerFile}
                              isEditMode={isInEditMode}
                              onSave={handleImageEditorSave}
                              onDirtyStateChange={setIsDirty}
                              onLoaded={() => {}}
                            />
                          ) : (
                            <ViewerImage
                              key={`img-${file?.id}-${imageBlobOverride ? 'v2' : 'v1'}`}
                              file={{
                                ...viewerFile,
                                decryptedFileBlob:
                                  imageBlobOverride ?? viewerFile.decryptedFileBlob,
                              }}
                              onLoaded={() => {}}
                            />
                          );

                        case FileTypeEnum.Audio:
                          return <ViewerAudio file={viewerFile} onLoaded={() => {}} />;

                        case FileTypeEnum.Video:
                          // viewerFile.streamUrl is set when the token allows HTTP Range streaming
                          // (see useViewer/videoStreaming.ts); otherwise the player uses the blob.
                          return (
                            <VideoViewer
                              file={viewerFile}
                              onLoaded={() => {}}
                              onRefreshStreamToken={refreshViewerToken}
                            />
                          );

                        case FileTypeEnum.Word:
                        case FileTypeEnum.Excel:
                        case FileTypeEnum.Powerpoint:
                        case FileTypeEnum.Pdf:
                          return isAntiScrapingActive ? (
                            <ViewerProtectedPdf
                              canDownloadFile={canDownloadFile ?? false}
                              bytes={pdfEncryptedBytes ?? new Blob()}
                            />
                          ) : (
                            <ViewerPdf
                              key={`pdf-${file?.id}-${pdfViewEpoch}`}
                              canDownloadFile={canDownloadFile ?? false}
                              fileBlob={pdfBlobOverride ?? decryptedFileBlob ?? new Blob()}
                              isSignMode={isSignMode}
                              signRef={pdfSignRef}
                              onDirtyStateChange={setIsDirty}
                              fileCufi={file?.cufi ?? ''}
                              fileId={file?.id}
                              fileName={file?.fileName ?? 'document.pdf'}
                              initialPage={pdfInitialPage}
                              initialScroll={pdfInitialScroll}
                              onSignUnavailable={handleSignUnavailable}
                              onSignSaved={handleSignSaved}
                            />
                          );

                        case FileTypeEnum.Secret:
                          return (
                            <ViewerFileSecret
                              ref={secretViewerRef}
                              file={viewerFile}
                              onLoaded={() => {}}
                              onModeChange={setSecretMode}
                            />
                          );

                        case FileTypeEnum.Note:
                          return (
                            <ViewerNote
                              ref={textViewerRef}
                              file={
                                noteFileNameOverride
                                  ? { ...viewerFile, fileName: noteFileNameOverride }
                                  : viewerFile
                              }
                              isEditMode={isInEditMode}
                              // Clean exit after a successful save (no unsaved-changes guard)
                              onSave={handleSaveFile}
                              onDirtyStateChange={setIsDirty}
                              onLoaded={() => {}}
                            />
                          );

                        case FileTypeEnum.Eml:
                          return <ViewerEml file={viewerFile} onLoaded={() => {}} />;

                        case FileTypeEnum.Ics:
                          return <ViewerIcs file={viewerFile} onLoaded={() => {}} />;

                        case FileTypeEnum.Vcf:
                          return <ViewerVcf file={viewerFile} onLoaded={() => {}} />;

                        case FileTypeEnum.Msg:
                          return <ViewerMsg file={viewerFile} onLoaded={() => {}} />;

                        default:
                          return (
                            <div className="flex h-full w-full items-center justify-center">
                              <Result
                                status="404"
                                title={t('viewer.error.noPreviewAvailable.title')}
                                subTitle={t('viewer.error.noPreviewAvailable.description')}
                                extra={
                                  file?.canDownload && parsedFile ? (
                                    <Button
                                      onClick={() =>
                                        handleDownloadAction(FileMenuActionEnum.Download)
                                      }
                                      type="primary"
                                    >
                                      {t('common.downloadFile')}
                                    </Button>
                                  ) : null
                                }
                              />
                            </div>
                          );
                      }
                    })()}
              </div>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default Viewer;
